import React, { useState, useEffect, useContext } from 'react';
import api from '../../../services/api';
import CompanyEditModal from '../../../components/modals/CompanyEditModal';
import UserEditModal from '../../../components/modals/UserEditModal';
import DeleteConfirmationModal from './modals/DeleteConfirmationModal';
import { maskBr } from 'js-brasil';
import DeleteIcon from '@mui/icons-material/Delete';
import { AuthContext } from '../../../context/AuthContext';

export default function SuperAdminCompanies() {
  const { user: currentUser } = useContext(AuthContext);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);

  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [userToEdit, setUserToEdit] = useState(null);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  const [nameFilter, setNameFilter] = useState(''); // Corrigido de tradeNameFilter
  const [cnpjFilter, setCnpjFilter] = useState(''); // Mantido, mas a lógica de filtro foi ajustada

  const fetchCompanies = async () => {
    try {
      setLoading(true);
      const res = await api.get('/companies');
      // Ordena as empresas por nome para melhor visualização
      res.data.sort((a, b) => a.name.localeCompare(b.name));
      setCompanies(res.data);
    } catch (err) {
      setError('Falha ao carregar empresas.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompanies();
  }, []);

  // Efeito para garantir que a barra de rolagem seja restaurada ao sair da página
  useEffect(() => {
    // A função retornada pelo useEffect é a função de limpeza.
    // Ela será executada quando o componente for desmontado (ao navegar para outra página).
    return () => {
      document.body.style.overflow = '';
    };
  }, []); // O array vazio [] garante que isso rode apenas na montagem e desmontagem do componente.

  const handleOpenModal = (company = null) => {
    setSelectedCompany(company); // Se for null, é criação
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedCompany(null);
  };

  const handleOpenUserModal = (user) => {
    setUserToEdit(user);
    setIsUserModalOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const handleCloseUserModal = () => {
    setIsUserModalOpen(false);
    setUserToEdit(null);
    document.body.style.overflow = '';
  };

  const handleSaveCompany = async (companyData) => {
    console.log("Iniciando handleSaveCompany com:", companyData);
    try {
      if (selectedCompany?.id) {
        // Modo de edição
        console.log(`Modo de edição para empresa slug: ${selectedCompany.slug}`);
        await api.put(`/companies/${selectedCompany.slug}`, companyData);
      } else {
        // Modo de criação
        console.log("Modo de criação. Enviando dados para o backend...");
        const response = await api.post('/companies', companyData);
        const { newAdmin } = response.data;
        // Se um novo admin foi criado, abre o modal de edição para ele.
        if (newAdmin) {
          console.log("Novo admin criado, abrindo modal de edição:", newAdmin);
          handleOpenUserModal(newAdmin);
        }
      }

      await fetchCompanies(); // Recarrega a lista de empresas
      handleCloseModal();

    } catch (err) {
      // Exibe a mensagem de erro vinda do backend, se disponível
      const errorMessage = err.response?.data?.msg || (selectedCompany?.id ? 'Falha ao atualizar empresa.' : 'Falha ao criar empresa.');
      console.error("Erro ao salvar empresa:", err.response?.data || err);
      alert(errorMessage); // Usando alert para feedback imediato do erro
    }
  };

  const handleSaveUser = async (dataToSave, isCreating) => {
    try {
      // O modal de usuário neste fluxo é sempre para edição
      if (userToEdit?.id) {
        await api.put(`/users/${userToEdit.userName}`, dataToSave);

        // Após salvar o usuário, recarregamos as empresas para garantir que
        // a lista (ex: nome do admin) esteja atualizada.
        const res = await api.get('/companies');
        res.data.sort((a, b) => a.name.localeCompare(b.name)); // Corrigido de tradeName para name
        setCompanies(res.data);
      }
      handleCloseUserModal();
    } catch (err) {
      console.error("Erro ao atualizar o usuário recém-criado:", err);
      throw err; // Re-throw para feedback no modal
    }
  };

  const handleDeleteClick = (company) => {
    setItemToDelete(company);
    setIsDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setItemToDelete(null);
    setIsDeleteModalOpen(false);
  };

  const handleConfirmDelete = async (company) => {
    try {
      await api.delete(`/companies/${company.slug}`); // Agora faz o soft-delete
      setCompanies(prev => prev.filter(c => c.id !== company.id));
      handleCloseDeleteModal();
    } catch (err) {
      alert(`Erro: ${err.response?.data?.message || 'Falha ao excluir empresa.'}`);
    }
  };

  const filteredCompanies = companies.filter(company => {
    const tradeNameMatch = !nameFilter || (company.name && company.name.toLowerCase().includes(nameFilter.toLowerCase())); // Corrigido para name
    const cnpjMatch = !cnpjFilter || (company.cnpjOrTaxId && company.cnpjOrTaxId.replace(/[^\d]/g, '').includes(cnpjFilter.replace(/[^\d]/g, ''))); // Corrigido para cnpjOrTaxId
    // For ADMIN_CLIENT, only show companies they administer
    const roleMatch = currentUser?.role === 'ADMIN_CLIENT' ? company.admins?.some(admin => admin.id === currentUser.id) : true;
    return tradeNameMatch && cnpjMatch && roleMatch;
  });

  if (loading) return <div className="loading-state"><div className="loading-spinner"></div> Carregando empresas...</div>;
  if (error) return <div className="error-message">{error}</div>;

  return (
    <>
      <div className="card" style={{ marginBottom: 'var(--space-6)' }}>
        <div className="card-body">
          <h3 className="filter-title">Filtros de Empresas</h3>
          <div className="filter-form" style={{ flexDirection: 'row', gap: 'var(--space-4)' }}>
            <div className="form-group" style={{ flex: 1 }}>
              <label htmlFor="name-filter" className="form-label">Buscar por Nome</label>
              <input
                id="name-filter"
                type="text"
                className="form-input"
                placeholder="Nome da empresa..."
                value={nameFilter}
                onChange={e => setNameFilter(e.target.value)}
                disabled={loading}
              />
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label htmlFor="cnpj-filter" className="form-label">Buscar por CNPJ</label>
              <input
                id="cnpj-filter"
                type="text"
                className="form-input"
                placeholder="00.000.000/0001-00"
                value={cnpjFilter}
                onChange={e => setCnpjFilter(e.target.value)}
                disabled={loading}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-4)' }}>
            <h2>Todas as Empresas ({filteredCompanies.length})</h2>
            {currentUser?.role === 'SUPER_ADMIN' || currentUser?.role === 'ADMIN_EAD' ? (
              <button className="btn btn-primary" onClick={() => handleOpenModal()}>
                + Criar Nova Empresa
              </button>
            ) : null}
          </div>
          <div className="table-wrapper">
            <table className="users-table">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>CNPJ</th>
                  <th>Admin Responsável</th>
                  <th>Plano</th>
                  <th style={{ width: '1%' }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {filteredCompanies.map(company => (
                  <tr key={company.slug} onClick={() => handleOpenModal(company)} style={{ cursor: 'pointer' }}>
                    <td>{company.name}</td>
                    <td>{company.cnpjOrTaxId ? maskBr.cnpj(company.cnpjOrTaxId) : 'Não informado'}</td>
                    <td>{company.admins?.map(a => a.fullName).join(', ') || 'Não informado'}</td>
                    <td>{company.subscribedPlan?.name || 'Não informado'}</td>
                    <td>
                      <div className="table-actions" onClick={e => e.stopPropagation()}>
                        <button className="btn btn-sm btn-secondary" onClick={() => handleOpenModal(company)}>Editar</button>
                        {currentUser?.role === 'SUPER_ADMIN' || currentUser?.role === 'ADMIN_EAD' ? (
                          <button className="btn btn-sm btn-danger" onClick={() => handleDeleteClick(company)} title="Excluir">
                            <DeleteIcon />
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {isModalOpen && (
        <CompanyEditModal
          company={selectedCompany}
          onClose={handleCloseModal}
          onSave={handleSaveCompany}
          currentUser={currentUser}
        />
      )}

      {isUserModalOpen && (
        <UserEditModal
          user={userToEdit}
          onClose={handleCloseUserModal}
          onSuccess={handleSaveUser}
          companies={companies}
        />
      )}

      {isDeleteModalOpen && (
        <DeleteConfirmationModal
          open={isDeleteModalOpen}
          onClose={handleCloseDeleteModal}
          onConfirmSuccess={handleConfirmDelete}
          itemType="company"
          item={itemToDelete}
        />
      )}
    </>
  );
}
